/** @format */

"use client";

import * as React from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from "@mui/material/styles";
import type { SxProps } from "@mui/material/styles";
import { ArrowClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ArrowClockwise";
import type { ApexOptions } from "apexcharts";
import { Chart } from "./Chart";
import { colors } from "../../../utility/types/constant";
import { CircularProgress } from "@mui/material";

export interface SalesProps {
	chartSeries: { name: string; data: number[] }[];
	categories?: string[] | number[];
	sx?: SxProps;
	onSync?: () => void;
	isSync?: boolean;
}

const ContributionsChart = ({
	chartSeries,
	categories,
	sx,
	onSync,
	isSync = false,
}: SalesProps): React.JSX.Element => {
	const chartOptions = useChartOptions(categories);

	return (
		<Card sx={sx}>
			<CardHeader
				action={
					<Button
						color="inherit"
						size="small"
						startIcon={
							isSync ?
								<CircularProgress size={16} />
							:	<ArrowClockwiseIcon fontSize="var(--icon-fontSize-md)" />
						}
						onClick={onSync}>
						{isSync ? "Syncing" : "Sync"}
					</Button>
				}
				title="Contributions"
			/>
			<CardContent>
				<Chart
					height={350}
					options={chartOptions}
					series={chartSeries}
					type="bar"
					width="100%"
				/>
			</CardContent>
			<Divider />
			{/* <CardActions sx={{ justifyContent: "flex-end" }}>
				<Button
					color="inherit"
					endIcon={<ArrowRightIcon fontSize="var(--icon-fontSize-md)" />}
					size="small">
					Overview
				</Button>
			</CardActions> */}
		</Card>
	);
};

function useChartOptions(categories?: string[] | number[]): ApexOptions {
	const theme = useTheme();

	// Default to day numbers 1-31 if no categories provided
	const defaultCategories = Array.from({ length: 31 }, (_, i) => i + 1);
	const xAxisCategories = categories || defaultCategories;

	return {
		chart: {
			background: "transparent",
			stacked: false,
			toolbar: { show: false },
		},
		colors: [colors.mainSecondary, alpha(colors.primary, 0.35)],
		dataLabels: { enabled: false },
		fill: { opacity: 1, type: "solid" },
		grid: {
			borderColor: theme.palette.divider,
			strokeDashArray: 2,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
		},
		legend: { show: false },
		plotOptions: { bar: { columnWidth: "40px" } },
		stroke: { colors: ["transparent"], show: true, width: 2 },
		theme: { mode: theme.palette.mode },
		xaxis: {
			axisBorder: { color: theme.palette.divider, show: true },
			axisTicks: { color: theme.palette.divider, show: true },
			categories: xAxisCategories,
			labels: {
				offsetY: 5,
				style: { colors: theme.palette.text.secondary },
				rotate: 0,
			},
			title: {
				text: "Day of Month",
				style: { color: theme.palette.text.secondary },
			},
		},
		yaxis: {
			labels: {
				formatter: (value) => {
					if (value === 0) return "0";
					const rounded = Math.round(value * 10) / 10; // Round to 1 decimal place
					return value >= 1 ? `${rounded}K` : `${rounded}`;
				},
				offsetX: -10,
				style: { colors: theme.palette.text.secondary },
			},
			title: {
				text: "",
				style: { color: theme.palette.text.secondary },
			},
		},
	};
}

export default ContributionsChart;
